import * as React from 'react';

import { Download, LucideCamera, RotateCw, Trash2 } from 'lucide-react';

import { useTranslations } from '../../../../i18n/useTranslations';
import { humanReadableDateTime } from '../../../../lib/humanReadables';
import { cn } from '../../../../lib/stylingUtils';

export interface PhotoCardProps {
  imageUrl?: string;
  fileName: string;
  timeStamp?: string;
  isUploading?: boolean;
  hasError?: boolean;
  onView?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  onRetry?: () => void;
  onDismissError?: () => void;
  isDownloading?: boolean;
  className?: string;
  width?: string;
  height?: string;
}

const PHOTO_CARD_DEFAULT_WIDTH = '267px';
const PHOTO_CARD_DEFAULT_HEIGHT = '240px';

const actionButtonBaseStyles =
  'flex size-9 items-center justify-center rounded-md text-white/100';

const actionButtonNeutralStyles = cn(
  actionButtonBaseStyles,
  'bg-neutral/60 hover:bg-neutral/70',
);

const actionButtonFailureStyles = cn(
  actionButtonBaseStyles,
  'bg-failure hover:bg-failure-darker',
);

type SpinnerProps = { label: string; variant: 'overlay' | 'skeleton' };

const Spinner = ({ label, variant }: SpinnerProps) => {
  const isOverlay = variant === 'overlay';

  return (
    <div
      className={cn(
        '@Spinner flex size-full flex-col items-center justify-center',
        isOverlay && 'absolute inset-0 bg-neutral/40',
      )}
    >
      <div
        className={cn(
          'mb-2 size-12 animate-spin rounded-full border-4',
          isOverlay
            ? 'border-white/30 border-t-white/80'
            : 'border-neutral/20 border-t-primary/60',
        )}
      />
      <span
        className={cn(
          'text-xs',
          isOverlay ? 'font-medium text-white/90' : 'text-neutral/50',
        )}
      >
        {label}
      </span>
    </div>
  );
};

type UploadingSkeletonProps = { children: React.ReactNode };

const UploadingSkeleton = ({ children }: UploadingSkeletonProps) => (
  <div className="@UploadingSkeleton flex size-full flex-col items-center justify-center bg-neutral/10">
    <div className="relative size-full animate-pulse bg-gradient-to-r from-neutral/5 via-neutral/20 to-neutral/5 bg-[length:200%_100%]">
      <div className="flex size-full flex-col items-center justify-center">
        {children}
      </div>
    </div>
  </div>
);

type EmptyStateProps = { label: string; className?: string };

const EmptyState = ({ label, className }: EmptyStateProps) => (
  <div
    className={cn(
      '@EmptyState',
      'flex size-full items-center justify-center bg-neutral/10',
      className,
    )}
  >
    <span className="text-neutral/50">{label}</span>
  </div>
);

type PhotoImageProps = { src: string; alt: string };

const PhotoImage = ({ src, alt }: PhotoImageProps) => (
  <img src={src} alt={alt} className="@PhotoImage size-full object-cover" />
);

type TimestampBadgeProps = { timestamp: string };

const TimestampBadge = ({ timestamp }: TimestampBadgeProps) => (
  <div className="@TimestampBadge absolute right-2 bottom-2 flex">
    <div className="flex items-center justify-center gap-2 rounded-md bg-neutral/60 p-1.5 text-sm text-white/100 hover:bg-neutral/70">
      <LucideCamera size={16} />
      <span>{humanReadableDateTime(timestamp)}</span>
    </div>
  </div>
);

type ErrorBadgeProps = { label: string };

const ErrorBadge = ({ label }: ErrorBadgeProps) => (
  <div className="@ErrorBadge absolute top-2 left-2 flex h-9 w-27 items-center justify-center rounded-md bg-failure text-white">
    {label}
  </div>
);

type ContentProps = {
  imageUrl?: string;
  fileName: string;
  isUploading: boolean;
  uploadingLabel: string;
  emptyLabel: string;
};

const PhotoContent = ({
  imageUrl,
  fileName,
  isUploading,
  uploadingLabel,
  emptyLabel,
}: ContentProps) => {
  if (isUploading) {
    if (imageUrl) {
      return (
        <>
          <PhotoImage src={imageUrl} alt={fileName} />
          <Spinner label={uploadingLabel} variant="overlay" />
        </>
      );
    }
    return (
      <UploadingSkeleton>
        <Spinner label={uploadingLabel} variant="skeleton" />
      </UploadingSkeleton>
    );
  }

  if (imageUrl) {
    return <PhotoImage src={imageUrl} alt={fileName} />;
  }

  return <EmptyState label={emptyLabel} className="@PhotoContent" />;
};

export const PhotoCard = ({
  imageUrl,
  fileName,
  timeStamp,
  isUploading = false,
  isDownloading = false,
  hasError = false,
  className,
  width = PHOTO_CARD_DEFAULT_WIDTH,
  height = PHOTO_CARD_DEFAULT_HEIGHT,
  onView,
  onDownload,
  onDelete,
  onRetry,
  onDismissError,
}: PhotoCardProps) => {
  const t = useTranslations();

  const showDefaultActions =
    !isUploading && !hasError && (onDownload || onDelete);
  const showErrorActions = hasError && (onRetry || onDismissError);
  const isViewable = !!onView && !isUploading && !hasError && !!imageUrl;

  return (
    <div
      className={cn(
        '@PhotoCard',
        'group relative overflow-hidden rounded-[8px] border border-neutral/20',
        isViewable && 'cursor-pointer',
        className,
      )}
      style={{ width, height }}
      onClick={isViewable ? onView : undefined}
      role={isViewable ? 'button' : undefined}
      tabIndex={isViewable ? 0 : undefined}
      aria-label={isViewable ? fileName : undefined}
      onKeyDown={
        isViewable
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onView?.();
              }
            }
          : undefined
      }
    >
      <PhotoContent
        imageUrl={imageUrl}
        fileName={fileName}
        isUploading={isUploading}
        uploadingLabel={t.components.photoCard.uploading}
        emptyLabel={t.components.photoCard.noImage}
      />

      {timeStamp && <TimestampBadge timestamp={timeStamp} />}

      {showDefaultActions && (
        <div className="absolute top-2 right-2 flex gap-2">
          {onDownload && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              className={actionButtonNeutralStyles}
              disabled={isDownloading}
              aria-label="Download photo"
            >
              <Download size={24} />
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className={actionButtonNeutralStyles}
              aria-label="Delete photo"
            >
              <Trash2 size={24} />
            </button>
          )}
        </div>
      )}

      {showErrorActions && (
        <>
          <ErrorBadge label={t.components.photoCard.uploadFailed} />
          <div className="absolute top-2 right-2 flex gap-2">
            {onRetry && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
                className={actionButtonFailureStyles}
                aria-label="Retry upload"
              >
                <RotateCw size={24} />
              </button>
            )}
            {onDismissError && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismissError();
                }}
                className={actionButtonNeutralStyles}
                aria-label="Discard photo"
              >
                <Trash2 size={24} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
